// Both imported from their defining modules rather than the `stores` barrel:
// the barrel pulls in the XState machines, which makes this tiny pure function
// impossible to load in a unit test. The type import is erased entirely.
import { findNodeNumberProblem } from '../../../stores/utils/build-workflow-errors';
import type { TableNode } from '../../../stores/table-store';
import type { ValidationFailureReason } from '../../../stores/utils/validation-outcome';

/**
 * Should this publish be refused because of the node numbers, and what should
 * the user be told? (PSTUDIO-4000 + PSTUDIO-3990)
 *
 * Extracted from `PublishModal.onPublish` so BOTH halves of the decision are
 * unit-testable. The guard itself cannot be reached from an e2e: it exists for
 * the `await waitForPendingSaves()` window, in which a coworker merge, a
 * cross-tab reload or a deferred panel commit rewrites `rows` after the Publish
 * button was already enabled — there is no UI gesture that produces it, because
 * with the rows in that state `canPublish` has the button disabled and the
 * modal cannot be opened at all. What a test CAN pin is this function: which
 * row shapes block, and that the message names the actual cause.
 *
 * Naming the cause is the point. The pre-existing toast always said "duplicate
 * node numbers", which for a blank cell is exactly the false claim this ticket
 * is about — the same defect as the phantom `Node: 0`, one layer up.
 */
export interface NodeNumberPublishBlock {
  /** i18n key under the `pro-studio/navigation` namespace. */
  key: string;
  defaultValue: string;
}

export const resolveNodeNumberPublishBlock = (
  rows: TableNode[]
): NodeNumberPublishBlock | null => {
  const problem = findNodeNumberProblem(rows);
  if (problem === null) return null;

  if (problem === 'duplicate') {
    return {
      key: 'publish_modal.toast.duplicate_node_numbers',
      defaultValue:
        'This solution has duplicate node numbers. Node numbers must be unique — fix the highlighted cells and try publishing again.',
    };
  }

  // A bad VALUE and an absent one get different wording, because the per-cell
  // error does: a cell holding '12.5' or `0` reads '"12.5" is not a usable node
  // number', so a toast calling the same row "without a node number"
  // contradicts what the user can see in the cell and sends them looking for an
  // empty one that is not there. Same defect class as the phantom `Node: 0`, one
  // layer up. `isBlankNodeNumberValue` is the single predicate that splits the
  // two, shared with the per-cell message so they cannot drift.
  //
  // The wording covers BOTH shapes this branch receives, which is why it does
  // not say "are not whole numbers": `0` is a whole number and still lands here
  // — it is what `sanitizeNodeNumber` writes for an unreadable cell and what
  // `convertToNumber` writes when a paste of 'hello' hits the column, never a
  // number the user picked. Saying "no usable node number … other than 0" is
  // true of `'12.5'`, of `'abc'` and of `0` alike (PSTUDIO-3990).
  if (problem === 'invalid-value') {
    return {
      key: 'publish_modal.toast.invalid_node_numbers',
      defaultValue:
        'This solution has cells that do not hold a usable node number. Enter a whole number other than 0 in each — fix the highlighted cells and try publishing again.',
    };
  }

  return {
    key: 'publish_modal.toast.missing_node_numbers',
    defaultValue:
      'This solution has rows without a node number. Every node needs one — fix the highlighted cells and try publishing again.',
  };
};

/**
 * The WHOLE pre-publish decision: the node-number problems above, plus the
 * verdict's own trustworthiness (PSTUDIO-4120).
 *
 * Why this exists rather than a second `if` in `onPublish`: the canon names TWO
 * primitives for this mechanism — the reactive `canPublish` getter AND this
 * pre-publish guard — and requires both to gain a new refusal in the same
 * change, because the guard covers the `await waitForPendingSaves()` window in
 * which a coworker merge or a cross-tab reload can rewrite `rows` (and finish a
 * validation pass) after the button was already enabled. Adding the refusal to
 * `canPublish` alone would have left the modal able to publish over a pass that
 * produced no verdict — the same fail-open the ticket closes, one layer up.
 *
 * Order is deliberate: a node-number problem is the MORE specific diagnosis and
 * names a cell the user can fix, so it wins. `validationFailure` is the general
 * case underneath it — the one that catches a thrown pass or a compiler that
 * answered with its own crash, where there is no offending cell to point at.
 */
export const resolvePublishBlock = (
  rows: TableNode[],
  validationFailure: ValidationFailureReason | null
): NodeNumberPublishBlock | null => {
  const nodeNumberBlock = resolveNodeNumberPublishBlock(rows);
  if (nodeNumberBlock) return nodeNumberBlock;

  if (validationFailure !== null) {
    return {
      key: 'publish_modal.toast.validation_incomplete',
      defaultValue:
        'The last validation run did not finish, so this solution has not been checked. Validation has been restarted — wait for it to complete, then try publishing again.',
    };
  }

  return null;
};
